import React, { useState } from 'react';
import { X, Plus, Sparkles, Download, Loader2, CheckCircle, Trash2, Maximize2, FileText, Table } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GridReview = ({ caseId, documents, onClose, caseData }: { caseId: any; documents: any; onClose: any; caseData?: any }) => {
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [columns, setColumns] = useState([]);
  const [newColumnPrompt, setNewColumnPrompt] = useState('');
  const [tableData, setTableData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [expandedCell, setExpandedCell] = useState(null);

  const toggleDocument = (docId) => {
    if (selectedDocs.includes(docId)) {
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } else {
      setSelectedDocs([...selectedDocs, docId]);
    }
  };

  const addColumn = () => {
    if (!newColumnPrompt.trim()) {
      toast.error('Skriv en kolonne-prompt');
      return;
    }
    
    setColumns([...columns, newColumnPrompt.trim()]);
    setNewColumnPrompt('');
    toast.success('Kolonne lagt til!');
  };

  const removeColumn = (index) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const generateTable = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Velg minst ett dokument');
      return;
    }
    
    if (columns.length === 0) {
      toast.error('Legg til minst én kolonne');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/documents/grid-review`, {
        case_id: caseId,
        document_ids: selectedDocs,
        columns: columns
      });

      setTableData(response.data);
      toast.success('Tabell generert!');
    } catch (error) {
      console.error('Grid review error:', error);
      toast.error('Kunne ikke generere tabell');
    } finally {
      setGenerating(false);
    }
  };

  const exportToCSV = () => {
    if (!tableData) return;

    const headers = ['Dokument', ...tableData.columns];
    const rows = tableData.rows.map(row => {
      const basicData = [row.filename];
      
      tableData.columns.forEach(col => {
        const cellData = row.cells[col];
        if (cellData && typeof cellData === 'object') {
          basicData.push(cellData.value || 'N/A');
        } else {
          basicData.push(cellData || 'N/A');
        }
      });
      
      return basicData;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grid_review_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('CSV lastet ned!');
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Fullscreen Modal */}
      <div className="fixed inset-0 md:inset-8 z-50 bg-white md:rounded-2xl shadow-2xl flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white md:rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-900 to-gray-700 rounded-xl flex items-center justify-center shadow-md">
              <Table className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Grid Review</h2>
              <p className="text-sm text-gray-600">Analyser flere dokumenter samtidig</p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* Left Sidebar - Setup */}
          <div className="w-full md:w-96 border-r border-gray-200 flex flex-col bg-gray-50">
            
            {/* Document Selection */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Documents */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                  1. Velg dokumenter ({selectedDocs.length})
                </h3>
                
                <div className="space-y-2">
                  {documents.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Ingen dokumenter tilgjengelig</p>
                    </div>
                  ) : (
                    documents.map((doc) => (
                      <label
                        key={doc.id}
                        className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                          selectedDocs.includes(doc.id)
                            ? 'bg-gray-900 text-white shadow-md'
                            : 'bg-white hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDocs.includes(doc.id)}
                          onChange={() => toggleDocument(doc.id)}
                          className="w-5 h-5 mt-0.5 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm truncate ${
                            selectedDocs.includes(doc.id) ? 'text-white' : 'text-gray-900'
                          }`}>
                            {doc.filename}
                          </div>
                          <div className={`text-xs truncate ${
                            selectedDocs.includes(doc.id) ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            {doc.file_type} • {(doc.file_size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Columns */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                  2. Legg til kolonner ({columns.length})
                </h3>
                
                <div className="space-y-2 mb-3">
                  {columns.map((col, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-3 bg-white rounded-xl border border-gray-200"
                    >
                      <div className="flex-1 text-sm font-medium text-gray-900 truncate">
                        {col}
                      </div>
                      <button
                        onClick={() => removeColumn(index)}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newColumnPrompt}
                    onChange={(e) => setNewColumnPrompt(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addColumn()}
                    placeholder="F.eks: 'Partier', 'Frister', 'Beløp'"
                    className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent placeholder:text-gray-400"
                  />
                  <button
                    onClick={addColumn}
                    className="p-2 bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {columns.length === 0 && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    Tips: Legg til kolonner som "Partier", "Datoer", "Beløp", etc.
                  </p>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <div className="p-6 border-t border-gray-200 bg-white">
              <button
                onClick={generateTable}
                disabled={generating || selectedDocs.length === 0 || columns.length === 0}
                className="w-full px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Genererer tabell...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generer Grid Review
                  </>
                )}
              </button>

              {selectedDocs.length === 0 && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Velg minst ett dokument
                </p>
              )}
              {columns.length === 0 && selectedDocs.length > 0 && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Legg til minst én kolonne
                </p>
              )}
            </div>
          </div>

          {/* Right Side - Table View */}
          <div className="flex-1 flex flex-col bg-white">
            
            {!tableData ? (
              <div className="flex-1 flex items-center justify-center p-12">
                <div className="text-center max-w-md">
                  <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Table className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Ingen tabell generert ennå</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Velg dokumenter og legg til kolonner for å starte analysen
                  </p>
                  <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-700">Velg 2+ dokumenter for best sammenligning</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-700">Legg til spesifikke kolonner du vil analysere</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-700">AI-en vil automatisk trekke ut relevant info</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Analyse fullført</h3>
                    <p className="text-sm text-gray-600">
                      {tableData.rows.length} dokumenter × {tableData.columns.length} kolonner
                    </p>
                  </div>
                  
                  <button
                    onClick={exportToCSV}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold rounded-xl flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Eksporter CSV
                  </button>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-auto p-6">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide border-r border-gray-200 bg-gray-100 sticky left-0 z-10">
                            Dokument
                          </th>
                          {tableData.columns.map((col, idx) => (
                            <th
                              key={idx}
                              className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide border-r border-gray-200 last:border-r-0 min-w-[200px]"
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tableData.rows.map((row, rowIdx) => (
                          <tr key={rowIdx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-200 bg-gray-50 sticky left-0 z-10">
                              <div className="max-w-[200px] truncate" title={row.filename}>
                                {row.filename}
                              </div>
                            </td>
                            {tableData.columns.map((col, colIdx) => {
                              const cellData = row.cells[col];
                              const value = typeof cellData === 'object' ? cellData?.value : cellData;
                              const context = typeof cellData === 'object' ? cellData?.context : null;
                              const cellKey = `${rowIdx}-${colIdx}`;
                              
                              return (
                                <td
                                  key={colIdx}
                                  className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 last:border-r-0 relative group"
                                >
                                  <div className="max-w-md">
                                    {value || (
                                      <span className="text-gray-400 italic">N/A</span>
                                    )}
                                  </div>
                                  
                                  {/* Context Tooltip */}
                                  {context && (
                                    <button
                                      onClick={() => setExpandedCell(expandedCell === cellKey ? null : cellKey)}
                                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-gray-900 text-white rounded text-xs hover:bg-gray-800 transition-all"
                                    >
                                      <Maximize2 className="w-3 h-3" />
                                    </button>
                                  )}
                                  
                                  {/* Expanded Context */}
                                  {expandedCell === cellKey && context && (
                                    <div className="absolute top-full left-0 mt-1 p-3 bg-gray-900 text-white text-xs rounded-xl shadow-xl z-20 w-80 max-w-md">
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <span className="font-semibold">Kontekst:</span>
                                        <button
                                          onClick={() => setExpandedCell(null)}
                                          className="hover:bg-gray-800 rounded p-0.5"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                      <p className="leading-relaxed">{context}</p>
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GridReview;
